import { useState, type ReactNode } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslate } from "@/lib/i18n";
import type { OffPremiseGridEntry } from "@/lib/api/types";

/**
 * What the dispatch controls need of a bill. The floor passes its grid
 * entries; the driver's settle panel passes a paid bill the floor no longer
 * lists (2026-10-27), built from the settlement card's bag.
 */
export type DispatchTarget = Pick<
  OffPremiseGridEntry,
  "invoiceId" | "dailySequence" | "status" | "channel" | "aggregatorId" | "customerPhone"
>;
import {
  dispatchActions,
  dispatchChip,
  isDispatchableBill,
} from "@/modules/pos/lib/dispatchBoard";
import { hasPermission, PERMISSIONS, useSession } from "@/modules/auth/useSession";
import type { InvoiceHandoff } from "@/modules/pos/pages/InvoicePage";
import { useDispatchAccess, useDispatchBoard } from "@/modules/pos/lib/useDispatchBoard";
import { AssignDriverSheet } from "./AssignDriverSheet";
import { DispatchProblemSheet } from "./DispatchProblemSheet";
import { DispatchStatusChip } from "./DispatchStatusChip";
import { TrackingLinkShare } from "./TrackingLinkShare";
import { isTrackingShareable } from "@/modules/pos/lib/trackingShare";

/**
 * The off-premise floor's dispatch layer (driver backend B3 · P13, P14), shared
 * by the PAPER Takeaway section and the classic rows so both looks say and
 * offer the same thing:
 *
 *  - `chip(bill)`: the driver line for an own-rider DELIVERY bag, for anyone
 *    who may read the board (`DISPATCH_MANAGE` or `DISPATCH_CASH_CONFIRM`);
 *  - `action(bill, look)`: for `DISPATCH_MANAGE` only, a SIBLING control, never
 *    inside the tile's own button — «عرض بلاغ المندوب» while the driver's
 *    report is open, «متابعة التوصيل» past pickup (end the delivery from the
 *    till, T16), else «إسناد إلى مندوب» / «تغيير المندوب» while the bag can
 *    still be (re)offered;
 *    Beside it, from the driver's accept until the bag is at the door, «مشاركة
 *    رابط التتبّع» (B4 · P16, `TrackingLinkShare`): the customer's tracking
 *    link, shared from the restaurant's own WhatsApp or copied;
 *  - `sheet`: the open picker or problem sheet;
 *  - `sitting`: the board for the rider's sitting (driver groups, P14);
 *  - `dispatch` / `access`: the board read and the grants (B6 · P24).
 *
 * A waiter (no dispatch grant) gets none of it and the board is never read.
 * The board is not read either while the floor has no delivery bag.
 *
 * G13 L4 (decision): a CLOSED bag that left with a TYPED rider and no
 * assignment stays assignable — the cashier may move it onto the app flow
 * (the driver then accepts and picks it up as usual, the bill being already
 * handed over); `handedTo` keeps the typed name (plan D-12's known limit).
 */
export function useFloorDispatch(bills: readonly OffPremiseGridEntry[]) {
  const t = useTranslate();
  const navigate = useNavigate();
  const { locale = "ar" } = useParams();
  const { data: session } = useSession();
  const canClose = hasPermission(session, PERMISSIONS.INVOICE_CLOSE);
  const access = useDispatchAccess();
  const dispatchable = bills.filter(isDispatchableBill);
  const dispatch = useDispatchBoard({ enabled: dispatchable.length > 0 });
  const [openFor, setOpenFor] = useState<{
    invoiceId: string;
    sequence: number;
    handedOver: boolean;
    sheet: "assign" | "followUp";
  } | null>(null);

  const chip = (bill: OffPremiseGridEntry): ReactNode => {
    if (!access.canSeeBoard || !isDispatchableBill(bill)) return null;
    const state = dispatchChip(dispatch.lookup(bill.invoiceId), dispatch.now, dispatch.skewMs);
    return state ? (
      <DispatchStatusChip chip={state} testId={`dispatch-chip-${bill.dailySequence}`} />
    ) : null;
  };

  /**
   * `handOver` (2026-10-20, the Delivery tab only): beside an ACCEPTED bag
   * still OPEN, «تسليم إلى المندوب» (the driver's name is on the chip) — the driver app is waiting for the
   * cashier to hand it over, and that act is the bill's close (T16). It opens
   * the bill on its own close / hand-over flow (`InvoiceHandoff.handOver`), so
   * the close's rules stay the bill's. For an `INVOICE_CLOSE` holder only.
   */
  const action = (
    bill: DispatchTarget,
    look: "paper" | "classic",
    options: { handOver?: boolean } = {},
  ): ReactNode => {
    if (!isDispatchableBill(bill) || !dispatch.board) return null;
    const entry = dispatch.lookup(bill.invoiceId);
    const current = entry?.current ?? null;
    // A paid bill is reached only for a bag still with a driver.
    if (bill.status === "SETTLED" && !current) return null;
    const handedOver = bill.status !== "OPEN";
    const handOver =
      options.handOver && canClose && bill.status === "OPEN" && current?.status === "ACCEPTED" ? (
        <button
          type="button"
          data-ui={look === "paper" ? "waiting-assign" : undefined}
          data-kind="handOver"
          data-testid={`dispatch-hand-over-${bill.dailySequence}`}
          aria-label={t((m) => m.dispatch.handOverToDriverAria, { sequence: bill.dailySequence })}
          onClick={() =>
            navigate(`/${locale}/pos/invoices/${bill.invoiceId}`, {
              state: { handOver: true } satisfies InvoiceHandoff,
            })
          }
          className={
            look === "paper"
              ? "flex items-center"
              : "shrink-0 rounded-[var(--radius-control)] border border-[color:var(--accent)] bg-[color:var(--pos-tile)] px-3 py-2 text-[length:var(--font-size-2xs)] font-extrabold text-[color:var(--accent)] transition-colors hover:text-[color:var(--text)]"
          }
        >
          {t((m) => m.dispatch.handOverToDriver)}
        </button>
      ) : null;
    // The gate (2026-10-20, Low 3): the hand-over only opens the close, so an
    // `INVOICE_CLOSE` holder gets it without the dispatch grant.
    if (!access.canManage) return handOver;
    const actions = dispatchActions(entry, { billHandedOver: handedOver });
    const followUp = actions.resolve || actions.end;
    if (!followUp && !actions.assign && !actions.reassign) return handOver;
    const share = isTrackingShareable(entry?.current?.status) ? (
      <TrackingLinkShare
        invoiceId={bill.invoiceId}
        sequence={bill.dailySequence}
        customerPhone={bill.customerPhone}
        look={look}
      />
    ) : null;
    const label = actions.resolve
      ? t((m) => m.dispatch.actionProblem)
      : actions.end
        ? t((m) => m.dispatch.actionFollowUp)
        : actions.assign
          ? t((m) => m.dispatch.actionAssign)
          : t((m) => m.dispatch.actionChange);
    return (
      <>
        {handOver}
        <button
          type="button"
          data-testid={`dispatch-action-${bill.dailySequence}`}
          data-kind={actions.resolve ? "problem" : actions.end ? "followUp" : actions.assign ? "assign" : "change"}
          onClick={() =>
            setOpenFor({
              invoiceId: bill.invoiceId,
              sequence: bill.dailySequence,
              handedOver,
              sheet: followUp ? "followUp" : "assign",
            })
          }
          className={[
            look === "paper"
              ? "w-full rounded-[var(--radius-control)] border bg-[color:var(--pos-tile)] px-2.5 py-[6px] text-[length:var(--font-size-2xs)] font-extrabold transition-colors hover:text-[color:var(--text)]"
              : "shrink-0 rounded-[var(--radius-control)] border bg-[color:var(--pos-tile)] px-3 py-2 text-[length:var(--font-size-2xs)] font-extrabold transition-colors hover:border-[color:var(--accent)] hover:text-[color:var(--text)]",
            actions.resolve
              ? "border-[color:var(--danger)] text-[color:var(--danger)]"
              : look === "paper"
                ? "border-[color:var(--pos-tile-br,var(--border-strong))] text-[color:var(--text-secondary)]"
                : "border-[color:var(--border-strong)] text-[color:var(--text-secondary)]",
          ].join(" ")}
        >
          {label}
        </button>
        {share}
      </>
    );
  };

  // The sheet carries its own bill facts: a paid bill is not on the floor.
  const sheet = !openFor ? null : openFor.sheet === "followUp" ? (
    <DispatchProblemSheet
      sequence={openFor.sequence}
      entry={dispatch.lookup(openFor.invoiceId)}
      billHandedOver={openFor.handedOver}
      onReassign={() => setOpenFor({ ...openFor, sheet: "assign" })}
      onClose={() => setOpenFor(null)}
    />
  ) : (
    <AssignDriverSheet
      invoiceId={openFor.invoiceId}
      sequence={openFor.sequence}
      entry={dispatch.lookup(openFor.invoiceId)}
      board={dispatch.board}
      onClose={() => setOpenFor(null)}
    />
  );

  /** The board for the rider's sitting, only for a session that may read it. */
  const sitting =
    access.canSeeBoard && dispatch.board
      ? { board: dispatch.board, lookup: dispatch.lookup }
      : undefined;

  /* B6 · P24: the board itself and who may use it, for the Delivery tab's
     waiting table (the same query, so the tab and the tiles agree). */
  return { chip, action, sheet, sitting, dispatch, access };
}
